import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';

import { FeedKey, ContentFragment } from 'api';
import { ContentProvider } from 'providers';
import {
  DataTable,
  DataTableActions,
  DataTableColumn,
  EmptyState,
  Numeric,
  PostImage,
} from 'components';
import { useCurrentUser, useGetFeed } from 'hooks';
import { DEFAULT_SORT } from 'app-constants';
import { ShareButton } from 'features/content/widgets';
import { IMAGE_HEIGHT } from 'features/activity/constants';

import ListLoader from './ListLoader';
import DateCell from './Date';
import PostCell from './Post';

/**
 * TODO: Pagination for this query is busted server-side
 * offset param isn't respected
 */
const MyPosts: FC = () => {
  const currentUser = useCurrentUser();
  const { t } = useTranslation();

  const { results, isEmpty, isInitialLoad, sentinelRef } = useGetFeed({
    sort: DEFAULT_SORT,
    key: FeedKey.Privateuser,
    id: currentUser.id,
    limit: 25,
  });

  if (isInitialLoad) {
    return <ListLoader />;
  }

  if (isEmpty) {
    return <EmptyState name="myPosts" />;
  }

  const pagination = {
    isInitialLoad,
    sentinelRef,
    isExhausted: true,
  };

  const columns: DataTableColumn<ContentFragment>[] = [
    {
      width: 170,
      headerCell: {
        render: t('sharing:postedOn'),
      },
      bodyCell: {
        render: content => <DateCell time={content.publish_at} />,
      },
    },
    {
      width: IMAGE_HEIGHT + 16,
      headerCell: {
        padding: 'dense',
        render: t('common:post'),
      },
      bodyCell: {
        padding: 'dense',
        render: content => <PostImage size={IMAGE_HEIGHT} content={content} />,
      },
    },
    {
      width: 300,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: content => <PostCell content={content} />,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:like_plural'),
      },
      bodyCell: {
        render: content => <Numeric value={content.reaction_count} />,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:comment_plural'),
      },
      bodyCell: {
        render: content => <Numeric value={content.comment_count} />,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:share_plural'),
      },
      bodyCell: {
        render: content => <Numeric value={content.share_count} />,
      },
    },
  ];

  const actions: DataTableActions<ContentFragment> = content => {
    return (
      <Grid wrap="nowrap" container alignItems="center" justify="flex-end">
        <Grid item>
          <ContentProvider value={content}>
            <ShareButton size="medium" />
          </ContentProvider>
        </Grid>
      </Grid>
    );
  };

  return (
    <DataTable
      data={results}
      columns={columns}
      actions={actions}
      pagination={pagination}
      rowSelection={null}
    />
  );
};

export default MyPosts;
